// ** Axios Imports
import axios from 'axios'
// ** Third Party Components

// const url = 'http://localhost:3100/api/'
const url = process.env.REACT_APP_API ? process.env.REACT_APP_API : 'http://localhost:3100/api/'

const tokenHandle = () => {

    const authentication = window.localStorage.getItem('userData')
    // console.log("Authentication : ", JSON.parse(authentication).access_token)
    return authentication ? JSON.parse(authentication).access_token : null
}

const refreshToken = async () => {
    const config = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json;charset=UTF-8',
            Authorization: `Bearer ${JSON.parse(window.localStorage.getItem('userData')).access_token}`
        },
        body: JSON.stringify({
            refreshToken: JSON.parse(window.localStorage.getItem('userData')).refresh_token
        })
    }
    const response = await fetch(`${url}user/refresh`, config)
    if (response.status >= 400) {
        // error
        window.localStorage.removeItem('userData')
        window.localStorage.removeItem('rootState')
        localStorage.setItem('needRefresh', true)
        window.location = '/login'
        return false
    } else {
        const res = await response.json()
        // ** success: persist the refreshed token back into userData so the next
        // ** request uses a valid token. Without this, every request keeps starting
        // ** with a stale (expired) token -> 401 -> refresh -> retry, doubling calls.
        const userData = JSON.parse(window.localStorage.getItem('userData')) || {}
        const newAccess = res?.data?.access_token ?? res?.access_token ?? res?.accessToken
        const newRefresh = res?.data?.refresh_token ?? res?.refresh_token ?? res?.refreshToken
        if (newAccess) {
            userData.access_token = newAccess
            if (newRefresh) userData.refresh_token = newRefresh
            window.localStorage.setItem('userData', JSON.stringify(userData))
        }
        return true
    }

}

const apiRequest = async (method, path, params, retry = false) => {
    // ** keep the untouched path so a retry rebuilds the URL from scratch
    const originalPath = path
    let config = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json;charset=UTF-8',
            Authorization: `Bearer ${tokenHandle()}`
        }
    }
    if (method === 'POST' || method === 'PUT' || method === 'DELETE') {
        config = {
            method: method,
            headers: {
                'Content-Type': 'application/json;charset=UTF-8',
                Authorization: `Bearer ${tokenHandle()}`
            },
            body: JSON.stringify(params)
        }
        const response = await fetch(url + path, config)
        if (response.status === 401 && !retry) {
            // ** Unauthorized: access token expired. The request was rejected before
            // ** the server processed it, so refreshing the token and retrying ONCE
            // ** is safe. We must NOT retry other error statuses (e.g. 500): a
            // ** non-idempotent POST/PUT/DELETE may already have been applied and a
            // ** retry would duplicate the data.
            const refresh = await refreshToken()
            if (refresh) {
                return await apiRequest(method, originalPath, params, true)
            }
        } else if (response.status <= 400) {
            const res = await response.json()
            console.log(`${method} ${path} response : `, res)
            return res
        }
    }
    if (method === 'GET') {
        if (params) {
            if (path === 'application/get' ||
                path === 'applicationfee/get' ||
                path === 'user/get' ||
                path === 'course/get' ||
                path === 'otherfee/get' ||
                path === 'promotion/get' ||
                path === 'branch/get' ||
                path === 'vehicle/get' ||
                path === 'list-payroll/get' ||
                path === 'payroll/user' ||
                path === 'list-expenses/get' ||
                path === 'income/get' ||
                path === 'expenses/get' ||
                path === 'list-expenses/get' ||
                path === 'evaluate/get'
            ) {
                path = `${path}/${params}`
            } if (path === 'payroll/userhistory') {
                path = `${path}/${params?.user_id}?month=${params?.month}`
            } else {
                path = `${path}?${new URLSearchParams(params)}`
            }
        }
        // const response = await axios.get(url + path, config)
        // const response = await fetch(url + path, config)
        let response 
        if (path === "mockreadercard") {
            response = await fetch("http://localhost:21998/readCard")
        } else {
          response = await fetch(url + path, config)
        }
        if (response.status === 401 && !retry) {
            // ** Unauthorized: refresh the token and retry the request ONCE.
            // ** originalPath is used so the query string is rebuilt cleanly instead
            // ** of being appended on top of the already-mutated path.
            const refresh = await refreshToken()
            if (refresh) {
                return await apiRequest(method, originalPath, params, true)
            }
        } else if (response.status <= 400) {
            const res = await response.json()
            console.log(`${method} ${path} response : `, res)
            return res
        }
    }

}

// ** In-flight request de-duplication.
// ** If the EXACT same request (same method + path + body) is already running,
// ** return the existing promise instead of firing a second network call. This
// ** collapses accidental double-submits (double click, touch "ghost tap", an
// ** onSubmit that fires twice in the same tick) into a single backend call, so
// ** the server can no longer create duplicate records from one user action.
const inFlight = new Map()

// ** Global "busy" tracking for write requests (POST/PUT/DELETE).
// ** UI components subscribe via useApiBusy() to disable submit buttons and show
// ** a spinner while a mutation is running, so the user gets feedback and cannot
// ** fire the same action again. GET requests are NOT counted so a background
// ** refetch never blocks the forms.
let pendingMutations = 0
const busyListeners = new Set()

const emitBusy = () => {
    const busy = pendingMutations > 0
    busyListeners.forEach((listener) => listener(busy))
}

export const getApiBusy = () => pendingMutations > 0

export const subscribeApiBusy = (listener) => {
    busyListeners.add(listener)
    // ** return an unsubscribe fn so effects can clean up
    return () => busyListeners.delete(listener)
}

const apiHandle = (method, path, params, retry = false) => {
    const key = `${method}|${path}|${params === undefined ? '' : JSON.stringify(params)}`
    const existing = inFlight.get(key)
    if (existing) {
        return existing
    }
    const isMutation = method === 'POST' || method === 'PUT' || method === 'DELETE'
    if (isMutation) {
        pendingMutations++
        emitBusy()
    }
    const promise = apiRequest(method, path, params, retry).finally(() => {
        inFlight.delete(key)
        if (isMutation) {
            pendingMutations--
            emitBusy()
        }
    })
    inFlight.set(key, promise)
    return promise
}

// ** Multipart upload (e.g. Excel imports). Kept separate from apiRequest
// ** because the body is a FormData instance: it must NOT be JSON.stringify'd
// ** and must NOT set a Content-Type header (the browser adds the multipart
// ** boundary itself).
const apiUploadRequest = async (path, formData, retry = false) => {
    const config = {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${tokenHandle()}`
        },
        body: formData
    }
    const response = await fetch(url + path, config)
    if (response.status === 401 && !retry) {
        const refresh = await refreshToken()
        if (refresh) {
            return await apiUploadRequest(path, formData, true)
        }
    } else if (response.status <= 400) {
        const res = await response.json()
        console.log(`POST ${path} response : `, res)
        return res
    }
}

export const apiUpload = (path, formData) => {
    pendingMutations++
    emitBusy()
    return apiUploadRequest(path, formData).finally(() => {
        pendingMutations--
        emitBusy()
    })
}

export default apiHandle
